'use client';
import _extends from '@babel/runtime/helpers/extends';
import React__default, { forwardRef, useState, useCallback, useEffect, useRef, Fragment } from 'react';
import classnames from 'classnames';
import { flushSync } from 'react-dom';
import { throttle } from '../../utils/timing.js';
import { Button } from '../Button/Button.js';
import { IconChevronLeft } from '../Icons/Directional/ChevronLeft.js';
import { IconChevronRight } from '../Icons/Directional/ChevronRight.js';
import { clientSide } from '../../utils/dom.js';

const Carousel = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    children,
    config,
    ...rest
  } = _ref;
  const {
    autoplay,
    controls,
    loop = false,
    animationType = 'slide',
    slidesToShow = 1,
    slidesToMove = 1,
    initialSlide = 0,
    gap = 0,
    overflow = 'hidden',
    infinite = false,
    breakpoints,
    onSlideChange,
    beforeSlideChange,
    afterSlideChange
  } = config || {};
  const {
    enabled: apEnabled = false,
    transitionSpeed = 500,
    delayTime: autoplayDelayTime = 3000
  } = autoplay || {};
  const [autoplayTransitionSpeed, setAutoplayTransitionSpeed] = useState(transitionSpeed);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(apEnabled);
  const [adaptiveHeight, setAdaptiveHeight] = useState(null);
  const allChilren = React__default.Children.toArray(children);
  const allSlides = (() => {
    if (infinite) {
      return [...allChilren, ...allChilren, ...allChilren];
    }
    return allChilren;
  })();
  const [activeIndex, setActiveIndex] = useState(() => {
    if (infinite) return allChilren.length + initialSlide;
    return initialSlide;
  });

  // Setup responsive breakpoints
  const getBreakpointConfig = useCallback(() => {
    if (!clientSide || !breakpoints || animationType !== 'slide') return {};
    const breakpointValues = Object.keys(breakpoints).sort((a, b) => Number(a) - Number(b));
    let targetWidth;
    for (let i = 0; i < breakpointValues.length; i++) {
      const width = Number(breakpointValues[i]);
      const next = Number(breakpointValues[i + 1]);
      const isFirst = i === 0;
      const isLast = breakpointValues.length - 1 === i;
      if (isFirst && window.innerWidth < width) {
        targetWidth = width;
        break;
      } else if (!isLast && window.innerWidth >= width && window.innerWidth < next) {
        targetWidth = width;
        break;
      } else if (isLast && window.innerWidth >= width) {
        targetWidth = width;
        break;
      }
    }
    const matchedConfig = targetWidth ? breakpoints[targetWidth] : {};
    const breakpointConfig = {
      ...matchedConfig,
      disabled: matchedConfig?.disabled === undefined ? false : matchedConfig.disabled
    };
    return breakpointConfig;
  }, [breakpoints, animationType]);
  const [currentConfig, setCurrentConfig] = useState({
    ...{
      slidesToShow: animationType === 'slide' ? slidesToShow : 1,
      slidesToMove: animationType === 'slide' ? slidesToMove : 1,
      loop,
      gap,
      overflow
    },
    ...getBreakpointConfig()
  });

  // Go to slide logic
  const updateIndex = useCallback(newIndex => {
    const totalSlides = allSlides.length;
    const upperLimit = allChilren.length - currentConfig.slidesToShow;
    if (infinite) {
      const slideIndexes = totalSlides - 1;
      const remainingSlides = slideIndexes - newIndex;
      if (remainingSlides < currentConfig.slidesToShow) {
        const backIndex = activeIndex - allChilren.length;
        flushSync(() => setAutoplayTransitionSpeed(0));
        flushSync(() => setActiveIndex(backIndex));
        flushSync(() => setAutoplayTransitionSpeed(transitionSpeed));
        if (beforeSlideChange) beforeSlideChange(backIndex + currentConfig.slidesToShow);
        setActiveIndex(backIndex + currentConfig.slidesToShow);
      } else if (activeIndex < currentConfig.slidesToShow) {
        const forwardIndex = 2 * allChilren.length + activeIndex;
        flushSync(() => setAutoplayTransitionSpeed(0));
        flushSync(() => setActiveIndex(forwardIndex));
        flushSync(() => setAutoplayTransitionSpeed(transitionSpeed));
        if (beforeSlideChange) beforeSlideChange(forwardIndex - currentConfig.slidesToShow);
        setActiveIndex(forwardIndex - currentConfig.slidesToShow);
      } else {
        if (beforeSlideChange) beforeSlideChange(newIndex - currentConfig.slidesToShow);
        setActiveIndex(newIndex);
      }
    } else {
      if (newIndex < 0) {
        newIndex = loop ? totalSlides - currentConfig.slidesToShow : 0;
      } else if (newIndex > upperLimit) {
        newIndex = loop ? totalSlides - currentConfig.slidesToShow : upperLimit;
      }
      if (beforeSlideChange) beforeSlideChange(newIndex);
      setActiveIndex(newIndex);
    }
    if (afterSlideChange) afterSlideChange(newIndex);
    if (onSlideChange) onSlideChange(newIndex);
  }, [activeIndex, infinite, loop, currentConfig.slidesToShow, beforeSlideChange, afterSlideChange, onSlideChange, allSlides.length, allChilren.length, transitionSpeed]);

  // disable autoplay if disabled prop is true
  useEffect(() => {
    if (!infinite && currentConfig.disabled !== undefined) {
      setAutoPlayEnabled(!currentConfig.disabled);
    }
  }, [currentConfig.disabled, infinite]);

  // Resize breakpoints.
  useEffect(() => {
    if (breakpoints) {
      const handleResize = throttle(() => {
        const breakpointConfig = getBreakpointConfig();
        if (Object.keys(breakpointConfig).length) {
          setCurrentConfig({
            ...currentConfig,
            ...breakpointConfig
          });
        }
      }, 200);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [breakpoints, currentConfig, getBreakpointConfig]);

  // Auto Play
  const autoplayIntervalRef = useRef();
  const setAutoplayInterval = useCallback(() => {
    if (!autoPlayEnabled) return;
    autoplayIntervalRef.current = setInterval(() => {
      const nextIndex = (activeIndex + currentConfig.slidesToMove) % allChilren.length;
      updateIndex(nextIndex);
    }, autoplayDelayTime);
  }, [activeIndex, autoPlayEnabled, autoplayDelayTime, allChilren.length, currentConfig.slidesToMove, updateIndex]);
  const handleHover = () => {
    if (autoPlayEnabled) clearInterval(autoplayIntervalRef.current);
  };
  const handleMouseLeave = () => {
    setAutoplayInterval();
  };
  useEffect(() => {
    if (autoPlayEnabled) setAutoplayInterval();
    return () => {
      if (autoPlayEnabled) clearInterval(autoplayIntervalRef.current);
    };
  }, [autoPlayEnabled, setAutoplayInterval]);

  // Buttons
  const handleButtonClick = nextIndex => {
    let updatedIndex = nextIndex;
    if (loop) {
      updatedIndex = nextIndex % allSlides.length;
      if (activeIndex !== 0 && activeIndex < currentConfig.slidesToMove) {
        updatedIndex = 0;
      }
    }
    if (autoPlayEnabled) {
      clearInterval(autoplayIntervalRef.current);
      setAutoPlayEnabled(false);
    }
    updateIndex(updatedIndex);
  };

  // Touch
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [draggingOffset, setDraggingOffset] = useState(0);
  const handleTouchStart = e => {
    setAutoPlayEnabled(false);
    setStartX(e.touches[0].clientX);
  };
  const handleTouchMove = e => {
    if (currentConfig.disabled) return;
    const deltaX = e.touches[0].clientX - startX;
    setCurrentX(e.touches[0].clientX);
    // Convert deltaX to a percentage based on container width
    const offsetPercentage = deltaX / e.currentTarget.clientWidth * currentConfig.slidesToShow * 100;
    setDraggingOffset(offsetPercentage);
  };
  const handleTouchEnd = () => {
    if (currentConfig.disabled || currentX === 0) return;

    // Calculate touch direction
    const deltaX = currentX - startX;

    // If the touch movement exceeds a certain threshold (e.g., 50 pixels),
    // consider it a swipe.
    if (deltaX > 50) {
      // Next slide
      handleButtonClick(activeIndex - currentConfig.slidesToMove);
    } else if (deltaX < -50) {
      // Previous slide
      handleButtonClick(activeIndex + currentConfig.slidesToMove);
    }
    // Reset positions
    setStartX(0);
    setCurrentX(0);
    setDraggingOffset(0);
  };
  const handlePaginationClick = index => {
    if (currentConfig.disabled || index === activeIndex) return;
    handleButtonClick(index);
  };
  const getStyles = () => {
    if (animationType !== 'slide') return {};
    const offset = activeIndex * (100 / currentConfig.slidesToShow) - draggingOffset / currentConfig.slidesToShow;
    return {
      transform: `translateX(calc(-${offset}%))`,
      transition: `transform ${draggingOffset === 0 ? autoplayTransitionSpeed : 0}ms ease-out`,
      gap: `0 ${currentConfig.gap ? currentConfig.gap : 0}px`,
      width: `calc(100% + ${currentConfig.gap ? currentConfig.gap : 0}px)`
    };
  };
  const prevButton = controls?.buttons?.enabled === false || currentConfig.disabled ? null : /*#__PURE__*/React__default.createElement(Button, {
    "aria-label": "Previous slide",
    className: classnames('b-carousel__button', 'b-carousel__button--arrow', 'b-carousel__button--previous', `b-carousel__button--x-${controls?.type || 'inside'}-${controls?.buttons?.previous?.position?.x || 'left'}`, `b-carousel__button--y-${controls?.type || 'inside'}-${controls?.buttons?.previous?.position?.y || 'center'}`),
    onClick: () => {
      handleButtonClick(activeIndex - currentConfig.slidesToMove);
    },
    disabled: !infinite && !currentConfig.loop && !autoPlayEnabled && activeIndex <= 0,
    variant: controls?.buttons?.variant,
    color: controls?.buttons?.color,
    size: controls?.buttons?.size,
    icon: {
      component: IconChevronLeft,
      ...controls?.buttons?.previous?.icon
    },
    children: controls?.buttons?.previous?.children
  });
  const nextButton = controls?.buttons?.enabled === false || currentConfig.disabled ? null : /*#__PURE__*/React__default.createElement(Button, {
    "aria-label": "Next slide",
    className: classnames('b-carousel__button', 'b-carousel__button--arrow', 'b-carousel__button--next', `b-carousel__button--x-${controls?.type || 'inside'}-${controls?.buttons?.next?.position?.x || 'right'}`, `b-carousel__button--y-${controls?.type || 'inside'}-${controls?.buttons?.next?.position?.y || 'center'}`),
    onClick: () => {
      handleButtonClick(activeIndex + currentConfig.slidesToMove);
    },
    disabled: !infinite && !currentConfig.loop && !autoPlayEnabled && activeIndex >= allChilren.length - currentConfig.slidesToShow,
    variant: controls?.buttons?.variant,
    color: controls?.buttons?.color,
    size: controls?.buttons?.size,
    icon: {
      component: IconChevronRight,
      ...controls?.buttons?.next?.icon
    },
    children: controls?.buttons?.next?.children
  });
  const dotsToShow = Math.ceil((allChilren.length - currentConfig.slidesToShow) / currentConfig.slidesToMove) + 1;
  const pagination = dotsToShow <= 1 || currentConfig.disabled ? null : /*#__PURE__*/React__default.createElement("div", {
    className: "b-carousel__pagination"
  }, Array.from({
    length: dotsToShow
  }).map((_, index) => /*#__PURE__*/React__default.createElement("button", {
    "aria-label": `Go to slide ${index + 1}`,
    className: classnames('b-carousel__button', 'b-carousel__button--pagination', {
      'b-carousel__button--active': slidesToShow >= 2 ? Math.ceil(activeIndex / currentConfig.slidesToMove) === index : activeIndex === index
    }),
    key: index,
    onClick: () => handlePaginationClick(index * currentConfig.slidesToMove)
  }, index + 1)));
  const controlsContainer = currentConfig.disabled ? null : /*#__PURE__*/React__default.createElement("div", {
    className: classnames('b-carousel__controls', `b-carousel__controls--${controls?.type || 'outside'}`, `b-carousel__controls--${controls?.position || 'bottom-center'}`, {
      'b-carousel__controls--grouped': controls?.grouped || controls?.grouped === undefined
    })
  }, (controls?.grouped || controls?.grouped === undefined) && prevButton, !infinite && controls?.pagination?.enabled !== false && dotsToShow <= (controls?.pagination?.max || 8) && pagination, (controls?.grouped || controls?.grouped === undefined) && nextButton);
  return /*#__PURE__*/React__default.createElement("div", _extends({
    role: "region",
    "aria-label": "Carousel",
    className: classnames('b-carousel__container', {
      [`b-carousel__container--reverse`]: controls?.position?.includes('top')
    }),
    "data-testid": "carousel"
  }, rest), /*#__PURE__*/React__default.createElement("div", {
    className: classnames('b-carousel__wrapper', {
      'b-carousel__wrapper--absolute-buttons': controls?.grouped === false
    })
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "b-carousel__overflow",
    style: {
      overflow: currentConfig.overflow
    }
  }, /*#__PURE__*/React__default.createElement("div", {
    className: classnames('b-carousel', `b-carousel--${animationType}`),
    style: {
      ...getStyles()
    },
    onMouseEnter: handleHover,
    onMouseLeave: handleMouseLeave,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    ref: el => {
      if (el && el.clientHeight > (adaptiveHeight || 0)) {
        setAdaptiveHeight(el.clientHeight);
      }
    }
  }, allSlides.map((child, index) => {
    const isSlideVisible = index >= activeIndex && index < activeIndex + currentConfig.slidesToShow;
    return /*#__PURE__*/React__default.createElement("div", {
      key: index,
      "aria-hidden": !isSlideVisible,
      className: `b-carousel__slide b-carousel__slide--${isSlideVisible ? 'active' : 'inactive'}`,
      style: {
        width: `calc(${100 / currentConfig.slidesToShow}% - ${currentConfig.gap ? currentConfig.gap : 0}px)`,
        transform: animationType === 'fade' ? `translateX(-${index * 100}%)` : 'none'
      }
    }, child);
  }))), controls?.buttons?.enabled !== false || controls?.grouped === false && /*#__PURE__*/React__default.createElement(Fragment, null, prevButton, nextButton)), controlsContainer);
});
Carousel.displayName = 'Carousel';

export { Carousel };
